import { create } from 'zustand'
import { royaleApi } from '../services/royaleApi'
import useAppStore from '../../../store/useAppStore'

const useRoyaleStore = create((set, get) => ({
  // State
  activeRound: null,
  bets: [],
  users: [],
  isLoading: false,
  isBetting: false,
  error: null,
  inventory: [],
  balance: 0,
  pendingBetIds: [],
  confirmedBetKeys: new Set(),
  lastValidRound: null,
  consecutiveErrors: 0,

  // Actions
  setActiveRound: (round) => set({ activeRound: round }),
  setBets: (bets) => set({ bets }),
  setUsers: (users) => set({ users }),
  setInventory: (inventory) => set({ inventory }),
  setBalance: (balance) => set({ balance }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  resetErrors: () => set({ consecutiveErrors: 0 }),
  hasConfirmedBetKey: (betKey) => get().confirmedBetKeys.has(betKey),
  addConfirmedBetKey: (betKey) => {
    const { confirmedBetKeys } = get()
    if (confirmedBetKeys.has(betKey)) return
    const nextKeys = new Set(confirmedBetKeys)
    nextKeys.add(betKey)
    set({ confirmedBetKeys: nextKeys })
  },

  // Добавление pending bet ID
  addPendingBetId: (betId) => {
    const { pendingBetIds } = get()
    if (!pendingBetIds.includes(betId)) {
      set({ pendingBetIds: [...pendingBetIds, betId] })
    }
  },

  // Удаление pending bet ID
  removePendingBetId: (betId) => {
    const { pendingBetIds } = get()
    set({ pendingBetIds: pendingBetIds.filter((id) => id !== betId) })
  },

  /**
   * Загрузка активного раунда (polling)
   */
  loadActiveRound: async (signal) => {
    try {
      set({ isLoading: true, error: null })
      const round = await royaleApi.getActiveRound(signal)

      set({
        activeRound: round,
        users: round.users || [],
        bets: round.bets || [],
        lastValidRound: round,
        consecutiveErrors: 0,
        isLoading: false
      })

      return round
    } catch (error) {
      const { consecutiveErrors, lastValidRound } = get()
      console.error('Ошибка загрузки активного раунда Royale:', error)
      set({
        error: error.message || 'Ошибка загрузки раунда',
        consecutiveErrors: consecutiveErrors + 1,
        isLoading: false
      })

      // Возвращаем последнее валидное состояние, чтобы UI не мигал
      return lastValidRound
    }
  },

  /**
   * Загрузка инвентаря для ставки
   */
  loadBetInventory: async () => {
    try {
      set({ isLoading: true, error: null })
      const data = await royaleApi.getBetInventory()

      set({
        inventory: data.inventory || [],
        balance: Number(data.balance || 0),
        isLoading: false
      })

      return data
    } catch (error) {
      console.error('Ошибка загрузки инвентаря Royale:', error)
      set({
        error: error.message || 'Инвентарь недоступен',
        isLoading: false
      })
      throw error
    }
  },

  /**
   * Отправка ставки
   */
  placeBet: async ({ roundId, crystal, items }) => {
    try {
      set({ isBetting: true, error: null })
      const response = await royaleApi.placeBet({ roundId, crystal, items })

      set({ isBetting: false })

      if (response.bet_id) {
        get().addPendingBetId(response.bet_id)
      }

      // Обновляем баланс пользователя из общего стора
      const appStore = useAppStore.getState()
      if (appStore.user?.balance != null) {
        set({ balance: Number(appStore.user.balance) })
      }

      return response
    } catch (error) {
      console.error('Ошибка отправки ставки Royale:', error)
      set({
        error: error.message || 'Ошибка отправки ставки',
        isBetting: false
      })
      throw error
    }
  },

  /**
   * Загрузка истории раундов
   */
  loadHistory: async () => {
    try {
      set({ isLoading: true, error: null })
      const [all, mine] = await Promise.all([
        royaleApi.getHistoryAll(),
        royaleApi.getHistoryMine()
      ])

      set({ isLoading: false })
      return { all, mine }
    } catch (error) {
      console.error('Ошибка загрузки истории Royale:', error)
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  /**
   * Загрузка завершенного раунда по ID
   */
  loadRoundById: async (roundId) => {
    try {
      set({ isLoading: true, error: null })
      const round = await royaleApi.getRoundById(roundId)
      set({ isLoading: false })
      return round
    } catch (error) {
      console.error('Ошибка загрузки раунда Royale по ID:', error)
      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  }
}))

export default useRoyaleStore
